/*
 * Site roles & permissions are defined here.
 * - A site permission is a resource & action pairing (e.g., site.read)
 * - A site role is a set of values for each site permission
 *
 * Resources are the various components of a site that we want to control with
 * permissions - a site itself is also a resource - and these resources are the
 * top-level keys in the SitePermissions type.
 *
 * Actions are the various things you can do with a resource - most commonly,
 * these are basic CRUD actions (create, read, update, delete), but we use other
 * verbs as well (e.g., publish, share, approve) where it makes sense based on
 * the resource. We have conventions around two specific verbs:
 * - `manage` is used to group together read, create, update, and delete actions
 *    and is generally used to represent "full access" to that type of resource
 * - `modify` is used to group together read, update, and delete actions, and is
 *   useful when there are various create flows that need to be controlled
 *   differently from each other
 *
 * Naming is difficult! In practice, some things won't necessarily fit into this
 * simplistic paradigm, but aim to keep resources aligned with other objects in
 * our data model (e.g., site, page, collection, etc.) and if necessary, use
 * action verbs that are clear about their scope. For example, site pages are
 * a specific resource and site page templates are a type of site page. Rather
 * than creating a separate resource to represent site page templates, we use
 * actions like `createTemplate` and `manageTemplate`. This inherently muddies
 * the action <-> resource pairing, so it is important to document your code.
 *
 * Similarly, this system is inherently flat. Nested resources are not supported
 * at this time. If you need to control access to a nested resource, you should
 * flatten it out and use a compound resource name (e.g., `sitePageSettings`).
 */

import {getEnumFromObjectKeys} from '@packages/utilities/zod';
import type {BaseRoleNameValues} from './additionalPermissions';
/**
 * Statically defined roles that can be saved to the database by name, ordered
 * by general level of access, from most to least.
 */
export const SAVED_SITE_ROLES = {
  project_admin: 'project_admin',
  designer: 'designer',
  branch_designer: 'branch_designer',
  page_builder: 'page_builder',
  page_builder_v2: 'page_builder_v2',
  editor: 'editor',
  commenter: 'commenter',
  viewer: 'viewer', // this should not be here
  custom: 'custom',
} as const;

/**
 * All statically defined roles, which includes our preview mode 'public' role.
 */
export const SITE_ROLES = {
  ...SAVED_SITE_ROLES,
  public: 'public',
} as const;

export const siteRoles = getEnumFromObjectKeys(SITE_ROLES);

/**
 * Site roles that are available to all Workspace plans
 */
export const DEFAULT_SITE_ROLES = [
  SITE_ROLES.project_admin,
  SITE_ROLES.designer,
  SITE_ROLES.page_builder_v2, // marketer
  SITE_ROLES.editor,
  SITE_ROLES.commenter,
];

/**
 * The legacy site role is the same as the project admin role. This was used
 * during the transition from before roles existed in order to preserve what
 * users used to be able to do on a site. Today, this should not be used and
 * usages of it are actively being removed. Please do not add more usages of
 * this.
 *
 * @deprecated
 */
export const LEGACY_SITE_ROLE = SITE_ROLES.project_admin;

export type SiteRolesType = keyof typeof SITE_ROLES;

export type SimulatableRolesType =
  | typeof SITE_ROLES.page_builder_v2
  | typeof SITE_ROLES.editor;

// @ts-expect-error Type 'string[]' is not assignable to type '("project_admin" | "designer" | "branch_designer" | "page_builder" | "page_builder_v2" | "editor" | "commenter" | "viewer" | "public")[]'.
export const SITE_ROLE_KEYS: Array<SiteRolesType> = Object.keys(SITE_ROLES);

export type SiteRolesForSelectType = Exclude<
  SiteRolesType,
  'public' | 'viewer'
>;

export const BASE_ROLE_TO_SITE_ROLE = {
  design: SITE_ROLES.designer,
  buildPages: SITE_ROLES.page_builder_v2,
  editContent: SITE_ROLES.editor,
  review: SITE_ROLES.commenter,
} as const satisfies Record<BaseRoleNameValues, SiteRolesType>;

/**
 * Configure which site roles are allowed to transition to other site roles.
 */

export const ALLOWED_SITE_ROLES = {
  [SITE_ROLES.project_admin]: [
    SITE_ROLES.project_admin,
    SITE_ROLES.designer,
    SITE_ROLES.page_builder,
    SITE_ROLES.branch_designer,
    SITE_ROLES.custom,
    SITE_ROLES.editor,
    SITE_ROLES.page_builder_v2,
    SITE_ROLES.commenter,
  ],
  [SITE_ROLES.designer]: [
    SITE_ROLES.project_admin,
    SITE_ROLES.designer,
    SITE_ROLES.page_builder,
    SITE_ROLES.editor,
    SITE_ROLES.branch_designer,
    SITE_ROLES.custom,
    SITE_ROLES.page_builder_v2,
    SITE_ROLES.commenter,
  ],
  [SITE_ROLES.page_builder]: [
    SITE_ROLES.project_admin,
    SITE_ROLES.designer,
    SITE_ROLES.page_builder,
    SITE_ROLES.branch_designer,
    SITE_ROLES.custom,
    SITE_ROLES.editor,
    SITE_ROLES.page_builder_v2,
    SITE_ROLES.commenter,
  ],
  [SITE_ROLES.branch_designer]: [
    SITE_ROLES.project_admin,
    SITE_ROLES.designer,
    SITE_ROLES.page_builder,
    SITE_ROLES.branch_designer,
    SITE_ROLES.custom,
    SITE_ROLES.editor,
    SITE_ROLES.page_builder_v2,
    SITE_ROLES.commenter,
  ],
  [SITE_ROLES.custom]: [
    SITE_ROLES.project_admin,
    SITE_ROLES.designer,
    SITE_ROLES.page_builder,
    SITE_ROLES.branch_designer,
    SITE_ROLES.custom,
    SITE_ROLES.editor,
    SITE_ROLES.page_builder_v2,
    SITE_ROLES.commenter,
  ],
  [SITE_ROLES.editor]: [
    SITE_ROLES.editor,
    SITE_ROLES.page_builder_v2,
    SITE_ROLES.commenter,
  ],
  [SITE_ROLES.page_builder_v2]: [
    SITE_ROLES.editor,
    SITE_ROLES.page_builder_v2,
    SITE_ROLES.commenter,
  ],
  [SITE_ROLES.commenter]: [SITE_ROLES.commenter],
};

/**
 * List of site roles that are allowed for clients.
 */

export const CLIENT_ALLOWED_SITE_ROLES = [
  SAVED_SITE_ROLES.page_builder_v2,
  SAVED_SITE_ROLES.editor,
  SAVED_SITE_ROLES.commenter,
] as const;

export type ClientAllowedSiteRoles = (typeof CLIENT_ALLOWED_SITE_ROLES)[number];

/**
 * Type guard for `CLIENT_ALLOWED_SITE_ROLES`. Use this instead of casting when
 * checking whether a string-typed site role is one of the client-eligible
 * roles — narrows to `ClientAllowedSiteRoles` for downstream type-safe usage.
 */
export function isClientAllowedSiteRole(
  role: string | undefined | null
): role is ClientAllowedSiteRoles {
  return (CLIENT_ALLOWED_SITE_ROLES as readonly string[]).includes(role ?? '');
}

/**
 * Configure which site roles belong to which seat tier represented by entitlements
 * - `maxFullAccessSeats`: roles that cost full price
 * - `maxLimitedAccessSeats`: roles that cost less because they have limited capabilities
 * - `maxReviewerSeats`: roles that cost the least because they can only review/approve/comment
 *
 * The entitlements in Stigg are
 * maxFullAccessSeats: feature-max-full-access-seats
 * maxLimitedAccessSeats: feature-max-limited-access-seats
 * maxReviewerSeats: feature-commenters
 *
 * These entitlements may be renamed in https://webflow.atlassian.net/browse/PYMT-3193
 * To be used to help calculate when a billing event is generated when a user's role is
 * changed via the Workspace default site role or the site access page.
 */

export const TIER_SITE_ROLES = {
  maxFullAccessSeats: [
    SITE_ROLES.project_admin,
    SITE_ROLES.designer,
    SITE_ROLES.page_builder,
    SITE_ROLES.branch_designer,
  ],
  maxLimitedAccessSeats: [SITE_ROLES.editor, SITE_ROLES.page_builder_v2],
  maxReviewerSeats: [SITE_ROLES.commenter],
};

export const SEAT_TIER_ORDER = [
  'maxFullAccessSeats',
  'maxLimitedAccessSeats',
  'maxReviewerSeats',
];

export type AdCreativePermissions = Readonly<{
  manage: boolean;
  read: boolean;
}>;
export type SiteResourcePermissions = Readonly<{
  approve: boolean;
  design: boolean;
  delete: boolean;
  duplicate: boolean;
  export: boolean;
  publish: boolean;
  read: boolean;
  share: boolean;
  simulateRoles: boolean;
  subPublish: boolean;
  transfer: boolean;
  update: boolean;
}>;
export type SiteAIPermissions = Readonly<{use: boolean}>;
export type SiteAgentInstructionsPermissions = Readonly<{
  manage: boolean;
  read: boolean;
}>;
export type SiteAgenticWorkflowsPermissions = Readonly<{
  manage: boolean;
  read: boolean;
}>;
export type SiteActivityLogResourcePermissions = Readonly<{read: boolean}>;
export type SiteAeoAnalyticsPermissions = Readonly<{
  view: boolean;
  manage: boolean;
}>;
export type SiteAeoLLMConfigPermissions = Readonly<{
  view: boolean;
  manage: boolean;
}>;
export type SiteAeoBrandPermissions = Readonly<{
  view: boolean;
  manage: boolean;
}>;
export type SiteAeoRecommendationsPermissions = Readonly<{
  read: boolean;
  manage: boolean;
}>;
export type SiteAnalyzeAddonPermissions = Readonly<{manage: boolean}>;
export type SiteAnnotationResourcePermissions = Readonly<{
  manage: boolean;
  read: boolean;
}>;
export type SiteApiTokenPermissions = Readonly<{create: boolean}>;
export type SiteAppResourcePermissions = Readonly<{
  manage: boolean;
  install: boolean;
}>;
export type SiteBillingResourcePermissions = Readonly<{
  manage: boolean;
  read: boolean;
}>;
export type SiteBranchResourcePermissions = Readonly<{
  design: boolean;
  editContent: boolean;
  publish: boolean;
  manage: boolean;
  merge: boolean;
  mergeApproved: boolean;
  createStandalone: boolean;
  createFromTemplate: boolean;
}>;
export type SiteCampaignPermissions = Readonly<{
  manage: boolean;
  read: boolean;
}>;
export type SiteCollectionResourcePermissions = Readonly<{
  manage: boolean;
  read: boolean;
}>;
export type SiteCollectionItemResourcePermissions = Readonly<{
  manage: boolean;
  read: boolean;
  publish: boolean;
  queue: boolean;
}>;
export type SiteCommerceResourcePermissions = Readonly<{
  manage: boolean;
  read: boolean;
}>;
export type SiteCommerceDriversResourcePermissions = Readonly<{
  read: boolean;
  manage: boolean;
}>;
export type SiteCommerceItemResourcePermissions = Readonly<{
  manage: boolean;
  read: boolean;
  publish: boolean;
  queue: boolean;
}>;
export type SiteCustomCodeResourcePermissions = Readonly<{
  manage: boolean;
  read: boolean;
}>;
export type SiteCustomDomainResourcePermissions = Readonly<{
  read: boolean;
  manage: boolean;
}>;
export type SiteDesignerModesPermissions = Readonly<{
  design: boolean;
  edit: boolean;
  view: boolean;
}>;
export type SiteFileResourcePermissions = Readonly<{manage: boolean}>;
export type SiteFlowResourcePermissions = Readonly<{manage: boolean}>;
export type SiteFormResourcePermissions = Readonly<{
  manage: boolean;
  read: boolean;
}>;
export type SiteLibraryResourcePermissions = Readonly<{
  manage: boolean;
  share: boolean;
  addComponent: boolean;
}>;
export type SiteLinkResourcePermissions = Readonly<{manage: boolean}>;
export type SiteLocalePermissions = Readonly<{
  manage: boolean;
  read: boolean;
}>;
export type SiteClientResourcePermissions = Readonly<{
  read: boolean;
}>;
export type SiteMembershipResourcePermissions = Readonly<{
  read: boolean;
  update: boolean;
}>;
export type SiteMetaResourcePermissions = Readonly<{
  read: boolean;
  manage: boolean;
  accessAPI: boolean;
}>;
export type SiteOptimizeAddonPermissions = Readonly<{manage: boolean}>;
export type SiteOptimizeAnalyzeMetricEventPermissions = Readonly<{
  manage: boolean;
}>;
export type SiteOptimizeAudienceEventMetricPermissions = Readonly<{
  manage: boolean;
}>;
export type SiteOptimizeExperiencePermissions = Readonly<{manage: boolean}>;
export type SiteOptimizeIntegrationsPermissions = Readonly<{
  read: boolean;
  manage: boolean;
}>;
export type SiteOptimizeResultsPermissions = Readonly<{read: boolean}>;
export type SiteOptimizeVariationPermissions = Readonly<{manage: boolean}>;
export type SiteOptimizeVariationStatePermissions = Readonly<{manage: boolean}>;
export type SitePageResourcePermissions = Readonly<{
  build: boolean;
  create: boolean;
  createBranch: boolean;
  createFromTemplate: boolean;
  duplicate: boolean;
  editContent: boolean;
  manageTemplates: boolean;
  modify: boolean;
  sppPublishToProduction: boolean;
  sppPublishToStaging: boolean;
}>;
export type SitePageSettingsPermissions = Readonly<{
  manage: boolean;
  read: boolean;
  manageTemplates: boolean;
}>;
export type SiteProfileResourcePermissions = Readonly<{manage: boolean}>;
export type SiteRedirectsResourcePermissions = Readonly<{
  manage: boolean;
  read: boolean;
}>;
export type SiteReportResourcePermissions = Readonly<{read: boolean}>;
export type SiteSearchResourcePermissions = Readonly<{index: boolean}>;
export type SiteSecretResourcePermissions = Readonly<{manage: boolean}>;
export type SiteSnapshotResourcePermissions = Readonly<{manage: boolean}>;
export type SiteStyleBlockResourcePermissions = Readonly<{
  create: boolean;
  modify: boolean;
  modifyOwn: boolean;
}>;
export type SiteSymbolResourcePermissions = Readonly<{
  create: boolean;
  modify: boolean;
  manage: boolean;
  read: boolean;
}>;
export type SiteTrackingSettingsPermissions = Readonly<{
  manage: boolean;
  read: boolean;
}>;
export type SiteUserSystemsPermissions = Readonly<{
  manage: boolean;
  manageGroups: boolean;
  read: boolean;
  readGroups: boolean;
}>;
export type SiteVariableResourcePermissions = Readonly<{
  manage: boolean;
}>;
export type SiteWebhookResourcePermissions = Readonly<{manage: boolean}>;
export type SiteCosmicPermissions = Readonly<{read: boolean; manage: boolean}>;

export type SitePermissions = Readonly<{
  site: SiteResourcePermissions;
  siteAI: SiteAIPermissions;
  siteAgentInstructions: SiteAgentInstructionsPermissions;
  siteAgenticWorkflows: SiteAgenticWorkflowsPermissions;
  siteActivityLog: SiteActivityLogResourcePermissions;
  siteAeoAnalytics: SiteAeoAnalyticsPermissions;
  siteAeoLLMConfig: SiteAeoLLMConfigPermissions;
  siteAeoBrand: SiteAeoBrandPermissions;
  siteAeoRecommendations: SiteAeoRecommendationsPermissions;
  siteAnalyzeAddon: SiteAnalyzeAddonPermissions;
  siteAnnotation: SiteAnnotationResourcePermissions;
  siteApiClientToken: SiteApiTokenPermissions;
  siteApp: SiteAppResourcePermissions;
  siteBilling: SiteBillingResourcePermissions;
  siteBranch: SiteBranchResourcePermissions;
  // Grouped with siteCampaign rather than sorted, because ad creatives are
  // scoped through a campaign and the two carry identical values in every
  // role map below.
  adCreative: AdCreativePermissions;
  siteCampaign: SiteCampaignPermissions;
  siteClient: SiteClientResourcePermissions;
  siteCollection: SiteCollectionResourcePermissions;
  siteCollectionItem: SiteCollectionItemResourcePermissions;
  siteCommerce: SiteCommerceResourcePermissions;
  siteCommerceDrivers: SiteCommerceDriversResourcePermissions;
  siteCommerceItem: SiteCommerceItemResourcePermissions;
  siteCosmic: SiteCosmicPermissions;
  siteCustomCode: SiteCustomCodeResourcePermissions;
  siteCustomDomain: SiteCustomDomainResourcePermissions;
  siteDesignerModes: SiteDesignerModesPermissions;
  siteFile: SiteFileResourcePermissions;
  siteFlow: SiteFlowResourcePermissions;
  siteForm: SiteFormResourcePermissions;
  siteLibrary: SiteLibraryResourcePermissions;
  siteLink: SiteLinkResourcePermissions;
  siteLocale: SiteLocalePermissions;
  siteMembership: SiteMembershipResourcePermissions;
  siteMeta: SiteMetaResourcePermissions;
  siteOptimizeAddon: SiteOptimizeAddonPermissions;
  siteOptimizeAnalyzeMetricEvent: SiteOptimizeAnalyzeMetricEventPermissions;
  siteOptimizeAudienceEventMetric: SiteOptimizeAudienceEventMetricPermissions;
  siteOptimizeExperience: SiteOptimizeExperiencePermissions;
  siteOptimizeIntegrations: SiteOptimizeIntegrationsPermissions;
  siteOptimizeResults: SiteOptimizeResultsPermissions;
  siteOptimizeVariation: SiteOptimizeVariationPermissions;
  siteOptimizeVariationState: SiteOptimizeVariationStatePermissions;
  sitePage: SitePageResourcePermissions;
  sitePageSettings: SitePageSettingsPermissions;
  siteProfile: SiteProfileResourcePermissions;
  siteRedirects: SiteRedirectsResourcePermissions;
  siteReport: SiteReportResourcePermissions;
  siteSearch: SiteSearchResourcePermissions;
  siteSecret: SiteSecretResourcePermissions;
  siteSnapshot: SiteSnapshotResourcePermissions;
  siteStyleBlock: SiteStyleBlockResourcePermissions;
  siteSymbol: SiteSymbolResourcePermissions;
  siteUserSystems: SiteUserSystemsPermissions;
  siteTrackingSettings: SiteTrackingSettingsPermissions;
  siteVariable: SiteVariableResourcePermissions;
  siteWebhook: SiteWebhookResourcePermissions;
  // Thank you for alphabetizing.
}>;

/**
 * Contract to follow for defining a new site role. This type is used in this package and is not the
 * same as the server's SiteRole domain type, which is a manifestation of the persistence layer.
 * This type is used to define the roles that are available to users in the UI.
 */
export type SiteRole = {
  /**
   * This is the internal name of the role that is saved in the database and often referenced in
   * our codebase. Because user-facing names change, keeping this reference allows us to maintain
   * backwards compatibility and de-couple our data from the whims of Product UX.
   */
  key: SiteRolesType;
  /**
   * The customer-facing name, typically seen in the role select dropdown. Will also be referenced
   * in our public education materials and knowledge base articles.
   */
  displayName: string;
  /**
   * The customer-facing description of the role, typically seen in the role select dropdown.
   */
  description: string;
  /**
   * System roles cannot be manually selected and assigned to a user - they are implicitly assigned
   * in certain scenarios by the server (e.g., public role for unauthenticated users). User-facing
   * roles are not system roles.
   */
  isSystemRole: boolean;
  /**
   * The site permissions granted to this role. Start with the BaseSiteRolePermissions and build up
   * from there to adhere to a principle of least privilege.
   */
  permissions: SitePermissions;
  /**
   * The permission overrides pattern was created to support the publish toggle. It can be extended
   * to other permissions besides publish, but there are some nuances with it as a pattern since
   * permission toggles UX was quickly deprecated as a product feature and additional development
   * was done under the assumption it would only be supporting the publish permission override. As
   * a result, there'll need to be some updates to this pattern to support other permissions.
   * - `canOverride` determines if the toggle should be enabled or disabled
   * - `defaultPublish` is the publish value if not overridden
   * - `reason` a map of tooltips when the toggle for the keyed permission is disabled
   *
   * Please reach out to Access Control before considering extending permission overrides.
   */
  permissionOverrides: {
    canOverride: boolean;
    defaultPublish: boolean;
    reason: {
      publish: string;
    };
  };
  /**
   * The verb to use when describing the role when sending email invites.
   * Ex.
   * Designer - x invited you to design
   * Admin - x invited you to manage
   * Default - x invited you to access
   */
  roleVerb: string;
};

/**
 * Site permissions base to build upon when defining a new built-in site role.
 * All permissions should be `false` to adhere to principle of least privilege!
 */
export const BaseSiteRolePermissions: SitePermissions = {
  site: {
    read: false,
    design: false,
    delete: false,
    duplicate: false,
    export: false,
    publish: false,
    share: false,
    simulateRoles: false,
    subPublish: false,
    transfer: false,
    update: false,
    approve: false,
  },
  siteAI: {use: false},
  siteAgentInstructions: {manage: false, read: false},
  siteAgenticWorkflows: {manage: false, read: false},
  siteActivityLog: {read: false},
  siteAeoAnalytics: {view: false, manage: false},
  siteAeoLLMConfig: {view: false, manage: false},
  siteAeoBrand: {view: false, manage: false},
  siteAeoRecommendations: {read: false, manage: false},
  siteAnalyzeAddon: {manage: false},
  siteAnnotation: {manage: false, read: false},
  siteApiClientToken: {create: false},
  siteApp: {manage: false, install: false},
  siteBilling: {read: false, manage: false},
  siteBranch: {
    design: false,
    editContent: false,
    publish: false,
    manage: false,
    merge: false,
    mergeApproved: false,
    createStandalone: false,
    createFromTemplate: false,
  },
  adCreative: {manage: false, read: false},
  siteCampaign: {manage: false, read: false},
  siteClient: {read: false},
  siteCollection: {manage: false, read: false},
  siteCollectionItem: {
    manage: false,
    read: false,
    publish: false,
    queue: false,
  },
  siteCommerce: {manage: false, read: false},
  siteCommerceDrivers: {read: false, manage: false},
  siteCommerceItem: {
    manage: false,
    read: false,
    publish: false,
    queue: false,
  },
  siteCosmic: {read: false, manage: false},
  siteCustomCode: {manage: false, read: false},
  siteCustomDomain: {read: false, manage: false},
  siteDesignerModes: {design: false, edit: false, view: false},
  siteFile: {manage: false},
  siteFlow: {manage: false},
  siteForm: {manage: false, read: false},
  siteLibrary: {manage: false, share: false, addComponent: false},
  siteLink: {manage: false},
  siteLocale: {manage: false, read: false},
  siteMembership: {read: false, update: false},
  siteMeta: {read: false, manage: false, accessAPI: false},
  siteOptimizeAddon: {manage: false},
  siteOptimizeAnalyzeMetricEvent: {manage: false},
  siteOptimizeAudienceEventMetric: {manage: false},
  siteOptimizeExperience: {manage: false},
  siteOptimizeIntegrations: {read: false, manage: false},
  siteOptimizeResults: {read: false},
  siteOptimizeVariation: {manage: false},
  siteOptimizeVariationState: {manage: false},
  sitePage: {
    build: false,
    create: false,
    editContent: false,
    createBranch: false,
    createFromTemplate: false,
    duplicate: false,
    manageTemplates: false,
    modify: false,
    sppPublishToProduction: false,
    sppPublishToStaging: false,
  },
  sitePageSettings: {manage: false, read: false, manageTemplates: false},
  siteProfile: {manage: false},
  siteRedirects: {manage: false, read: false},
  siteReport: {read: false},
  siteSearch: {index: false},
  siteSecret: {manage: false},
  siteSnapshot: {manage: false},
  siteStyleBlock: {create: false, modify: false, modifyOwn: false},
  siteSymbol: {create: false, modify: false, manage: false, read: false},
  siteUserSystems: {
    manage: false,
    manageGroups: false,
    read: false,
    readGroups: false,
  },
  siteTrackingSettings: {manage: false, read: false},
  siteVariable: {manage: false},
  siteWebhook: {manage: false},
  // Thank you for alphabetizing.
};

/**
 * The project admin role has full access to all aspects of a site.
 *
 * @owner Access Control team
 */
export const ProjectAdminSitePermissions: SitePermissions = {
  ...BaseSiteRolePermissions,
  site: {
    read: true,
    design: true,
    delete: true,
    duplicate: true,
    export: true,
    publish: true,
    share: true,
    simulateRoles: true,
    subPublish: true,
    transfer: true,
    update: true,
    approve: true,
  },
  siteAI: {use: true},
  siteAgentInstructions: {manage: true, read: true},
  siteAgenticWorkflows: {manage: true, read: true},
  siteActivityLog: {read: true},
  siteAeoAnalytics: {view: true, manage: true},
  siteAeoLLMConfig: {view: true, manage: true},
  siteAeoBrand: {view: true, manage: true},
  siteAeoRecommendations: {read: true, manage: true},
  siteAnalyzeAddon: {manage: true},
  siteAnnotation: {manage: true, read: true},
  siteApiClientToken: {create: true},
  siteApp: {manage: true, install: true},
  siteBilling: {read: true, manage: true},
  siteBranch: {
    design: true,
    editContent: true,
    publish: true,
    manage: true,
    merge: true,
    mergeApproved: true,
    createStandalone: true,
    createFromTemplate: true,
  },
  adCreative: {manage: true, read: true},
  siteCampaign: {manage: true, read: true},
  siteClient: {read: true},
  siteCollection: {manage: true, read: true},
  siteCollectionItem: {
    manage: true,
    read: true,
    publish: true,
    queue: true,
  },
  siteCommerce: {manage: true, read: true},
  siteCommerceDrivers: {read: true, manage: true},
  siteCommerceItem: {manage: true, read: true, publish: true, queue: true},
  siteCosmic: {read: true, manage: true},
  siteCustomCode: {manage: true, read: true},
  siteCustomDomain: {read: true, manage: true},
  siteDesignerModes: {
    ...BaseSiteRolePermissions.siteDesignerModes,
    design: true,
    edit: true,
  },
  siteFile: {manage: true},
  siteFlow: {manage: true},
  siteForm: {manage: true, read: true},
  siteLibrary: {manage: true, share: true, addComponent: true},
  siteLink: {manage: true},
  siteLocale: {manage: true, read: true},
  siteMembership: {read: true, update: true},
  siteMeta: {read: true, manage: true, accessAPI: true},
  siteOptimizeAddon: {manage: true},
  siteOptimizeAnalyzeMetricEvent: {manage: true},
  siteOptimizeAudienceEventMetric: {manage: true},
  siteOptimizeExperience: {manage: true},
  siteOptimizeIntegrations: {read: true, manage: true},
  siteOptimizeResults: {read: true},
  siteOptimizeVariation: {manage: true},
  siteOptimizeVariationState: {manage: true},
  sitePage: {
    build: true,
    create: true,
    createBranch: true,
    createFromTemplate: true,
    editContent: true,
    duplicate: true,
    manageTemplates: true,
    modify: true,
    sppPublishToProduction: true,
    sppPublishToStaging: true,
  },
  sitePageSettings: {manage: true, read: true, manageTemplates: true},
  siteProfile: {manage: true},
  siteRedirects: {manage: true, read: true},
  siteReport: {read: true},
  siteSearch: {index: true},
  siteSecret: {manage: true},
  siteSnapshot: {manage: true},
  siteStyleBlock: {create: true, modify: true, modifyOwn: true},
  siteSymbol: {create: true, modify: true, manage: true, read: true},
  siteTrackingSettings: {manage: true, read: true},
  siteUserSystems: {
    manage: true,
    manageGroups: true,
    read: true,
    readGroups: true,
  },
  siteVariable: {manage: true},
  siteWebhook: {manage: true},
};
export const ProjectAdminSiteRole: SiteRole = {
  key: 'project_admin',
  displayName: 'Site manager',
  description: 'Can manage site settings and billing',
  isSystemRole: false,
  permissions: ProjectAdminSitePermissions,
  permissionOverrides: {
    canOverride: false,
    defaultPublish: ProjectAdminSitePermissions.site.publish,
    reason: {
      publish:
        'Site managers will always have permission to publish the whole site',
    },
  },
  roleVerb: 'manage',
};

/**
 * The designer role grants full control to a site's design across all contexts
 * (e.g., branch vs main pages).
 *
 * @owner Access Control team
 */
export const DesignerSitePermissions: SitePermissions = {
  ...BaseSiteRolePermissions,
  site: {
    ...BaseSiteRolePermissions.site,
    read: true,
    design: true,
    duplicate: true,
    export: true,
    publish: true,
    simulateRoles: true,
    subPublish: true,
    update: true,
    approve: true,
  },
  siteAI: {use: true},
  siteAgentInstructions: {manage: true, read: true},
  siteAgenticWorkflows: {manage: true, read: true},
  siteActivityLog: {read: true},
  siteAeoAnalytics: {view: true, manage: true},
  siteAeoLLMConfig: {view: true, manage: false},
  siteAeoBrand: {view: true, manage: true},
  siteAeoRecommendations: {read: true, manage: true},
  siteAnnotation: {manage: true, read: true},
  siteApp: {manage: true, install: true},
  siteBilling: {...BaseSiteRolePermissions.siteBilling, read: true},
  siteBranch: {
    design: true,
    editContent: true,
    publish: true,
    manage: true,
    merge: true,
    mergeApproved: true,
    createStandalone: true,
    createFromTemplate: true,
  },
  adCreative: {manage: true, read: true},
  siteCampaign: {manage: true, read: true},
  siteClient: {...BaseSiteRolePermissions.siteClient, read: true},
  siteCollection: {manage: true, read: true},
  siteCollectionItem: {
    manage: true,
    read: true,
    publish: true,
    queue: true,
  },
  siteCommerce: {manage: true, read: true},
  siteCommerceDrivers: {read: true, manage: true},
  siteCommerceItem: {manage: true, read: true, publish: true, queue: true},
  siteCosmic: {read: true, manage: true},
  siteCustomCode: {manage: true, read: true},
  siteCustomDomain: {read: true, manage: true},
  siteDesignerModes: {
    ...BaseSiteRolePermissions.siteDesignerModes,
    design: true,
    edit: true,
  },
  siteFile: {manage: true},
  siteFlow: {manage: true},
  siteForm: {manage: true, read: true},
  siteLibrary: {manage: true, share: true, addComponent: true},
  siteMembership: {...BaseSiteRolePermissions.siteMembership, read: true},
  siteMeta: {...BaseSiteRolePermissions.siteMeta, read: true, manage: true},
  siteLocale: {manage: true, read: true},
  siteOptimizeAnalyzeMetricEvent: {manage: true},
  siteOptimizeAudienceEventMetric: {manage: true},
  siteOptimizeExperience: {manage: true},
  siteOptimizeIntegrations: {
    ...BaseSiteRolePermissions.siteOptimizeIntegrations,
    read: true,
  },
  siteOptimizeResults: {read: true},
  siteOptimizeVariation: {manage: true},
  siteOptimizeVariationState: {manage: true},
  sitePage: {
    build: true,
    create: true,
    createBranch: true,
    createFromTemplate: true,
    duplicate: true,
    editContent: true,
    manageTemplates: true,
    modify: true,
    sppPublishToProduction: true,
    sppPublishToStaging: true,
  },
  sitePageSettings: {manage: true, read: true, manageTemplates: true},
  siteProfile: {manage: true},
  siteRedirects: {manage: false, read: true},
  siteReport: {read: true},
  siteSearch: {index: true},
  siteSecret: {manage: true},
  siteSnapshot: {manage: true},
  siteStyleBlock: {create: true, modify: true, modifyOwn: true},
  siteSymbol: {create: true, modify: true, manage: true, read: true},
  siteTrackingSettings: {manage: true, read: true},
  siteUserSystems: {
    manage: true,
    manageGroups: true,
    read: true,
    readGroups: true,
  },
  siteVariable: {manage: true},
  siteWebhook: {manage: true},
};
export const DesignerSiteRole: SiteRole = {
  key: 'designer',
  displayName: 'Designer',
  description: 'Design using all features',
  isSystemRole: false,
  permissions: DesignerSitePermissions,
  permissionOverrides: {
    canOverride: true,
    defaultPublish: DesignerSitePermissions.site.publish,
    reason: {publish: 'n/a'},
  },
  roleVerb: 'design',
};

/**
 * The limited designer role is mostly the same as the designer role, but with
 * one primary difference: the ability to modify classes made by other designers.
 *
 * Historical context: this was our first, scrappy attempt at a "page builder"
 * role, filling a need to have a more restricted designer.
 *
 * @owner Enterprise Workflows
 */
export const LimitedDesignerSitePermissions: SitePermissions = {
  ...BaseSiteRolePermissions,
  site: {
    ...BaseSiteRolePermissions.site,
    read: true,
    design: true,
    duplicate: true,
    export: true,
    update: true,
  },
  siteAI: {use: true},
  siteAgenticWorkflows: {manage: true, read: true},
  siteActivityLog: {read: true},
  siteAeoRecommendations: {read: true, manage: false},
  siteAnnotation: {manage: true, read: true},
  siteApp: {...BaseSiteRolePermissions.siteApp, manage: true},
  siteBilling: {...BaseSiteRolePermissions.siteBilling, read: true},
  siteBranch: {
    design: true,
    editContent: true,
    publish: true,
    manage: true,
    merge: true,
    mergeApproved: true,
    createStandalone: true,
    createFromTemplate: true,
  },
  adCreative: {manage: true, read: true},
  siteCampaign: {manage: false, read: true},
  siteClient: {...BaseSiteRolePermissions.siteClient, read: true},
  siteCollection: {manage: true, read: true},
  siteCollectionItem: {
    manage: true,
    read: true,
    publish: true,
    queue: true,
  },
  siteCommerce: {manage: true, read: true},
  siteCommerceDrivers: {read: true, manage: false},
  siteCommerceItem: {manage: true, read: true, publish: true, queue: true},
  siteCustomCode: {manage: true, read: true},
  siteCustomDomain: {read: true, manage: true},
  siteDesignerModes: {
    ...BaseSiteRolePermissions.siteDesignerModes,
    design: true,
    edit: true,
  },
  siteFile: {manage: true},
  siteForm: {manage: true, read: true},
  siteLibrary: {...BaseSiteRolePermissions.siteLibrary, addComponent: true},
  siteMembership: {...BaseSiteRolePermissions.siteMembership, read: true},
  siteMeta: {
    ...BaseSiteRolePermissions.siteMeta,
    read: true,
    manage: true,
  },
  siteOptimizeAnalyzeMetricEvent: {manage: true},
  siteOptimizeAudienceEventMetric: {manage: true},
  siteOptimizeExperience: {manage: true},
  siteOptimizeIntegrations: {
    ...BaseSiteRolePermissions.siteOptimizeIntegrations,
    read: true,
  },
  siteOptimizeResults: {read: true},
  siteOptimizeVariation: {manage: true},
  siteOptimizeVariationState: {manage: true},
  sitePage: {
    build: true,
    create: true,
    createBranch: true,
    createFromTemplate: true,
    duplicate: true,
    editContent: true,
    manageTemplates: true,
    modify: true,
    sppPublishToProduction: false,
    sppPublishToStaging: false,
  },
  sitePageSettings: {manage: true, read: true, manageTemplates: true},
  siteRedirects: {manage: false, read: true},
  siteReport: {read: true},
  siteSearch: {index: true},
  siteSecret: {manage: true},
  siteSnapshot: {manage: true},
  siteStyleBlock: {
    ...BaseSiteRolePermissions.siteStyleBlock,
    create: true,
    modifyOwn: true,
  },
  siteSymbol: {...BaseSiteRolePermissions.siteSymbol, read: true},
  siteTrackingSettings: {manage: true, read: true},
  siteUserSystems: {
    manage: true,
    manageGroups: true,
    read: true,
    readGroups: true,
  },
  siteWebhook: {manage: true},
};
export const LimitedDesignerSiteRole: SiteRole = {
  key: 'page_builder',
  displayName: 'Limited designer',
  description: 'Design without modifying classes and components',
  isSystemRole: false,
  permissions: LimitedDesignerSitePermissions,
  permissionOverrides: {
    canOverride: false,
    defaultPublish: LimitedDesignerSitePermissions.site.publish,
    reason: {
      publish:
        '`Users with Limited designer role will always be prevented from publishing the whole site',
    },
  },
  roleVerb: 'design',
};

/**
 * The branch designer site role is similar to the site designer role but is
 * limited to designing only on branches - not main pages of a site.
 *
 * @owner Enterprise Workflows team
 */
export const BranchDesignerSitePermissions: SitePermissions = {
  ...BaseSiteRolePermissions,
  site: {
    ...BaseSiteRolePermissions.site,
    read: true,
    duplicate: true,
    export: true,
  },
  siteAI: {use: true},
  siteAgenticWorkflows: {manage: false, read: true},
  siteActivityLog: {read: true},
  siteAeoRecommendations: {read: true, manage: false},
  siteAnnotation: {manage: true, read: true},
  siteApp: {...BaseSiteRolePermissions.siteApp, manage: true},
  siteBilling: {...BaseSiteRolePermissions.siteBilling, read: true},
  siteBranch: {
    ...BaseSiteRolePermissions.siteBranch,
    design: true,
    editContent: true,
    publish: true,
    manage: true,
    mergeApproved: true,
    createStandalone: true,
    createFromTemplate: true,
  },
  adCreative: {manage: false, read: true},
  siteCampaign: {manage: false, read: true},
  siteClient: {...BaseSiteRolePermissions.siteClient, read: true},
  siteCollection: {...BaseSiteRolePermissions.siteCollection, read: true},
  siteCollectionItem: {
    ...BaseSiteRolePermissions.siteCollectionItem,
    read: true,
  },
  siteCommerce: {...BaseSiteRolePermissions.siteCommerce, read: true},
  siteCommerceItem: {...BaseSiteRolePermissions.siteCommerceItem, read: true},
  siteCustomCode: {manage: true, read: true},
  siteCustomDomain: {...BaseSiteRolePermissions.siteCustomDomain, read: true},
  siteDesignerModes: {design: true, edit: true, view: true},
  siteFile: {manage: true},
  siteForm: {manage: true, read: true},
  siteLibrary: {...BaseSiteRolePermissions.siteLibrary, addComponent: true},
  siteLocale: {...BaseSiteRolePermissions.siteLocale, read: true},
  siteMembership: {...BaseSiteRolePermissions.siteMembership, read: true},
  siteMeta: {...BaseSiteRolePermissions.siteMeta, read: true, manage: true},
  sitePage: {
    ...BaseSiteRolePermissions.sitePage,
    build: true,
    createBranch: true,
    editContent: true,
  },
  sitePageSettings: {manage: true, read: true, manageTemplates: true},
  siteProfile: {manage: true},
  siteReport: {read: true},
  siteSearch: {index: true},
  siteSecret: {manage: true},
  siteSnapshot: {manage: true},
  siteStyleBlock: {create: true, modify: true, modifyOwn: true},
  siteSymbol: {create: true, modify: true, manage: true, read: true},
  siteTrackingSettings: {manage: false, read: true},
  siteUserSystems: {...BaseSiteRolePermissions.siteUserSystems},
  siteWebhook: {manage: true},
};
export const BranchDesignerSiteRole: SiteRole = {
  key: 'branch_designer',
  displayName: 'Designer (needs approval)',
  description: 'Design on branches and merge once approved',
  permissions: BranchDesignerSitePermissions,
  isSystemRole: false,
  permissionOverrides: {
    canOverride: false,
    defaultPublish: BranchDesignerSitePermissions.site.publish,
    reason: {
      publish:
        'Users with Designer (needs approval) role will always be prevented from publishing the whole site',
    },
  },
  roleVerb: 'design',
};

/**
 * The marketer site role (A.K.A page builder) is a more limited role than
 * Designer roles - by not granting permission to make design changes.
 *
 * @owner Collaboration team
 */
export const MarketerSitePermissions: SitePermissions = {
  ...BaseSiteRolePermissions,
  site: {
    ...BaseSiteRolePermissions.site,
    read: true,
    design: true,
    duplicate: true,
    publish: true,
    subPublish: true,
    update: true,
  },
  siteAI: {use: true},
  siteAgentInstructions: {manage: false, read: true},
  siteAgenticWorkflows: {manage: true, read: true},
  siteActivityLog: {read: true},
  siteAeoAnalytics: {view: true, manage: true},
  siteAeoLLMConfig: {view: true, manage: false},
  siteAeoBrand: {view: true, manage: true},
  siteAeoRecommendations: {read: true, manage: true},
  siteAnnotation: {manage: true, read: true},
  siteBilling: BaseSiteRolePermissions.siteBilling,
  siteBranch: {
    ...BaseSiteRolePermissions.siteBranch,
    createStandalone: true,
    design: true,
    editContent: true,
    publish: true,
    manage: true,
    createFromTemplate: true,
  },
  adCreative: {manage: true, read: true},
  siteCampaign: {manage: false, read: true},
  siteClient: {...BaseSiteRolePermissions.siteClient, read: true},
  siteCollection: {...BaseSiteRolePermissions.siteCollection, read: true},
  siteCollectionItem: {
    manage: true,
    read: true,
    publish: true,
    queue: true,
  },
  siteCommerce: {read: true, manage: true},
  siteCommerceDrivers: {read: true, manage: false},
  siteCommerceItem: {manage: true, read: true, publish: true, queue: true},
  siteCustomCode: {...BaseSiteRolePermissions.siteCustomCode},
  siteCustomDomain: {...BaseSiteRolePermissions.siteCustomDomain, read: true},
  siteDesignerModes: {...BaseSiteRolePermissions.siteDesignerModes, edit: true},
  siteFile: {manage: true},
  siteForm: {manage: true, read: true},
  siteLibrary: {...BaseSiteRolePermissions.siteLibrary, addComponent: true},
  siteMembership: {...BaseSiteRolePermissions.siteMembership, read: true},
  siteMeta: {
    ...BaseSiteRolePermissions.siteMeta,
    read: true,
    manage: true,
  },
  siteOptimizeAnalyzeMetricEvent: {manage: true},
  siteOptimizeAudienceEventMetric: {manage: true},
  siteOptimizeExperience: {manage: true},
  siteOptimizeIntegrations: {
    ...BaseSiteRolePermissions.siteOptimizeIntegrations,
    read: true,
  },
  siteOptimizeResults: {read: true},
  siteOptimizeVariation: {manage: true},
  siteOptimizeVariationState: {manage: true},
  sitePage: {
    ...BaseSiteRolePermissions.sitePage,
    build: true,
    create: true,
    createBranch: true,
    createFromTemplate: true,
    duplicate: true,
    editContent: true,
    modify: true,
    sppPublishToProduction: true,
    sppPublishToStaging: true,
  },
  sitePageSettings: {
    ...BaseSiteRolePermissions.sitePageSettings,
    manage: true,
    read: true,
  },
  siteReport: {read: true},
  siteSearch: {index: true},
  siteSecret: {manage: true},
  siteSymbol: {...BaseSiteRolePermissions.siteSymbol, modify: true, read: true},
  siteTrackingSettings: {manage: true, read: true},
  siteUserSystems: {...BaseSiteRolePermissions.siteUserSystems},
};
export const MarketerSiteRole: SiteRole = {
  key: 'page_builder_v2',
  displayName: 'Marketer',
  description: 'Build pages with components',
  permissions: MarketerSitePermissions,
  isSystemRole: false,
  permissionOverrides: {
    canOverride: true,
    defaultPublish: MarketerSitePermissions.site.publish,
    reason: {publish: 'n/a'},
  },
  roleVerb: 'build',
};

/**
 * Editor site role limits users to editing content on a site, and any other
 * necessary functions required for their jobs-to-be-done.
 *
 * @owner Collaboration team
 */
export const EditorSitePermissions: SitePermissions = {
  ...BaseSiteRolePermissions,
  site: {
    ...BaseSiteRolePermissions.site,
    read: true,
    design: true,
    duplicate: true,
    publish: true,
    subPublish: true,
    update: true,
  },
  siteAI: {use: true},
  siteAgentInstructions: {manage: false, read: true},
  siteAgenticWorkflows: {manage: true, read: true},
  siteActivityLog: {read: true},
  siteAeoAnalytics: {view: true, manage: true},
  siteAeoLLMConfig: {view: true, manage: false},
  siteAeoBrand: {view: true, manage: true},
  siteAeoRecommendations: {read: true, manage: true},
  siteAnnotation: {manage: true, read: true},
  siteBilling: BaseSiteRolePermissions.siteBilling,
  siteBranch: {
    ...BaseSiteRolePermissions.siteBranch,
    design: true,
    editContent: true,
    publish: true,
    manage: true,
  },
  adCreative: {manage: false, read: true},
  siteCampaign: {manage: false, read: true},
  siteClient: {...BaseSiteRolePermissions.siteClient, read: true},
  siteCollection: {...BaseSiteRolePermissions.siteCollection, read: true},
  siteCollectionItem: {
    manage: true,
    read: true,
    publish: true,
    queue: true,
  },
  siteCommerce: {read: true, manage: true},
  siteCommerceDrivers: {read: true, manage: false},
  siteCommerceItem: {manage: true, read: true, publish: true, queue: true},
  siteCosmic: {read: false, manage: false},
  siteCustomCode: {...BaseSiteRolePermissions.siteCustomCode},
  siteCustomDomain: {...BaseSiteRolePermissions.siteCustomDomain, read: true},
  siteDesignerModes: {...BaseSiteRolePermissions.siteDesignerModes, edit: true},
  siteFile: {manage: true},
  siteForm: {manage: true, read: true},
  siteLocale: {...BaseSiteRolePermissions.siteLocale, read: true},
  siteMembership: {...BaseSiteRolePermissions.siteMembership, read: true},
  siteMeta: {...BaseSiteRolePermissions.siteMeta, read: true, manage: true},
  siteOptimizeIntegrations: {
    ...BaseSiteRolePermissions.siteOptimizeIntegrations,
    read: true,
  },
  siteOptimizeResults: {read: true},
  siteOptimizeVariation: {manage: true},
  siteOptimizeVariationState: {manage: true},
  sitePage: {
    ...BaseSiteRolePermissions.sitePage,
    createBranch: true,
    editContent: true,
    sppPublishToProduction: true,
    sppPublishToStaging: true,
  },
  sitePageSettings: {
    ...BaseSiteRolePermissions.sitePageSettings,
    manage: true,
    read: true,
  },
  siteReport: {read: true},
  siteSearch: {index: true},
  siteSecret: {manage: true},
  siteSymbol: {...BaseSiteRolePermissions.siteSymbol, modify: true, read: true},
  siteTrackingSettings: {manage: false, read: true},
  siteUserSystems: {...BaseSiteRolePermissions.siteUserSystems},
};
export const EditorSiteRole: SiteRole = {
  key: 'editor',
  displayName: 'Content editor',
  description: 'Only edit content',
  isSystemRole: false,
  permissions: EditorSitePermissions,
  permissionOverrides: {
    canOverride: true,
    defaultPublish: EditorSitePermissions.site.publish,
    reason: {publish: 'n/a'},
  },
  roleVerb: 'edit',
};

/**
 *  Commenter site role limits users to basic viewing functions and commenting
 *  mode for Sites in the Designer.
 *
 *  @owner Collaboration team
 */
export const CommenterSitePermissions: SitePermissions = {
  ...BaseSiteRolePermissions,
  site: {...BaseSiteRolePermissions.site, read: true, approve: true},
  siteAgenticWorkflows: {manage: false, read: true},
  siteAeoAnalytics: {view: true, manage: false},
  siteAeoLLMConfig: {view: true, manage: false},
  siteAeoBrand: {view: true, manage: false},
  siteAeoRecommendations: {read: false, manage: false},
  siteAnnotation: {manage: true, read: true},
  adCreative: {manage: false, read: true},
  siteCampaign: {manage: false, read: true},
  siteClient: {...BaseSiteRolePermissions.siteClient, read: true},
  siteCollection: {...BaseSiteRolePermissions.siteCollection, read: true},
  siteCollectionItem: {
    ...BaseSiteRolePermissions.siteCollectionItem,
    read: true,
  },
  siteCommerce: {...BaseSiteRolePermissions.siteCommerce, read: true},
  siteCommerceItem: {...BaseSiteRolePermissions.siteCommerceItem, read: true},
  siteCustomCode: {...BaseSiteRolePermissions.siteCustomCode},
  siteCustomDomain: {...BaseSiteRolePermissions.siteCustomDomain, read: true},
  siteForm: {...BaseSiteRolePermissions.siteForm},
  siteLocale: {...BaseSiteRolePermissions.siteLocale, read: true},
  siteMembership: {...BaseSiteRolePermissions.siteMembership, read: true},
  siteMeta: {...BaseSiteRolePermissions.siteMeta, read: true},
  siteOptimizeResults: {read: true},
  siteOptimizeIntegrations: {
    ...BaseSiteRolePermissions.siteOptimizeIntegrations,
    read: true,
  },
  siteSymbol: {...BaseSiteRolePermissions.siteSymbol, read: true},
  siteTrackingSettings: {manage: false, read: true},
  siteUserSystems: {...BaseSiteRolePermissions.siteUserSystems},
};
export const CommenterSiteRole: SiteRole = {
  key: 'commenter',
  displayName: 'Reviewer',
  description: 'View and approve design changes',
  isSystemRole: false,
  permissions: CommenterSitePermissions,
  permissionOverrides: {
    canOverride: false,
    defaultPublish: CommenterSitePermissions.site.publish,
    reason: {
      publish:
        'Users with Reviewer role will always be prevented from publishing the whole site',
    },
  },
  roleVerb: 'review',
};

/**
 *  The viewer site role is a special role that is not currently used. It was
 *  initial intended to be leveraged for various site viewing scenarios in the
 *  Designer (e.g. preview workflow, share link, etc.).
 *
 *  @owner Access Control team
 */
export const ViewerSitePermissions: SitePermissions = {
  ...BaseSiteRolePermissions,
  site: {...BaseSiteRolePermissions.site, read: true},
  siteAgenticWorkflows: {manage: false, read: true},
  siteActivityLog: {read: true},
  adCreative: {manage: false, read: true},
  siteCampaign: {manage: false, read: true},
  siteCollection: {...BaseSiteRolePermissions.siteCollection, read: true},
  siteCollectionItem: {
    ...BaseSiteRolePermissions.siteCollectionItem,
    read: true,
  },
  siteCommerce: {...BaseSiteRolePermissions.siteCommerce, read: true},
  siteCommerceItem: {...BaseSiteRolePermissions.siteCommerceItem, read: true},
  siteReport: {read: true},
  siteLocale: {...BaseSiteRolePermissions.siteLocale, read: true},
  siteSymbol: {...BaseSiteRolePermissions.siteSymbol, read: true},
  siteTrackingSettings: {manage: false, read: true},
};
export const ViewerSiteRole: SiteRole = {
  key: 'viewer',
  displayName: 'Viewer',
  description: 'Only view',
  isSystemRole: true,
  permissions: ViewerSitePermissions,
  permissionOverrides: {
    canOverride: false,
    defaultPublish: ViewerSitePermissions.site.publish,
    reason: {
      publish:
        'Users with Viewer role will always be prevented from publishing the whole site',
    },
  },
  roleVerb: 'access',
};

/**
 * The public site role is a special role that is used for specific scenarios
 * determined by the server. It is assigned to unauthenticated users as a
 * baseline default when we do not have a way to identify a role for the user.
 *
 * It is not a role that can be manually assigned to a user.
 *
 * @owner Access Control team
 */
export const PublicSitePermissions: SitePermissions = {
  ...BaseSiteRolePermissions,
  site: {...BaseSiteRolePermissions.site, design: true},
  siteBranch: {...BaseSiteRolePermissions.siteBranch, design: true},
  siteCollection: {...BaseSiteRolePermissions.siteCollection, read: true},
  siteCollectionItem: {
    ...BaseSiteRolePermissions.siteCollectionItem,
    read: true,
  },
  siteAeoRecommendations: {
    ...BaseSiteRolePermissions.siteAeoRecommendations,
    read: true,
  },
  siteCommerce: {...BaseSiteRolePermissions.siteCommerce, read: true},
  siteCommerceItem: {...BaseSiteRolePermissions.siteCommerceItem, read: true},
  siteStyleBlock: {create: true, modify: true, modifyOwn: true},
  siteSymbol: {create: true, modify: true, manage: true, read: false},
};
export const PublicSiteRole: SiteRole = {
  key: 'public',
  displayName: 'Public',
  description: 'Users who are not logged in',
  isSystemRole: true,
  permissions: PublicSitePermissions,
  permissionOverrides: {
    canOverride: false,
    defaultPublish: PublicSitePermissions.site.publish,
    reason: {
      publish:
        'Users with Public role will always be prevented from publishing the whole site',
    },
  },
  roleVerb: 'access',
};

/**
 * List of all statically defined site roles.
 */
export const SITE_ROLES_LIST = [
  ProjectAdminSiteRole,
  DesignerSiteRole,
  BranchDesignerSiteRole,
  LimitedDesignerSiteRole,
  MarketerSiteRole,
  EditorSiteRole,
  CommenterSiteRole,
  ViewerSiteRole,
  PublicSiteRole,
];
export const SELECTABLE_SITE_ROLES = SITE_ROLES_LIST.filter(
  (role) => !role.isSystemRole
);
export const SITE_ROLE_OPTIONS = SITE_ROLES_LIST.map((role) => role.key);

/**
 * Given a site role identifier, returns the site permissions for that role.
 */
export function getSitePermissionsByRole(role: SiteRolesType): SitePermissions {
  switch (role) {
    case 'project_admin':
      return ProjectAdminSiteRole.permissions;
    case 'designer':
      return DesignerSiteRole.permissions;
    case 'branch_designer':
      return BranchDesignerSiteRole.permissions;
    case 'page_builder':
      return LimitedDesignerSiteRole.permissions;
    case 'page_builder_v2':
      return MarketerSiteRole.permissions;
    case 'editor':
      return EditorSiteRole.permissions;
    case 'commenter':
      return CommenterSiteRole.permissions;
    case 'viewer':
      return ViewerSiteRole.permissions;
    case 'public':
      return PublicSiteRole.permissions;
    default:
      throw new Error(`Unknown role for permissions evaluation: ${role}`);
  }
}

/**
 * Given a string identifier for a role, returns a site role or undefined if
 * no role exists with that key.
 *
 * @param roleKey the string that might be a role
 * @returns {SiteRole|undefined}
 */
export function toSiteRole(roleKey: string): SiteRole | null | undefined {
  return SITE_ROLES_LIST.find(
    (role) => role.key === roleKey.toLowerCase().trim()
  );
}

/**
 * Converts a string to a SiteRolesType or returns undefined if the string is not a valid role.
 *
 * @param roleKey the string that might be a role
 * @returns {SiteRolesType|undefined}
 */
export function toSiteRoleType(
  roleKey: string | undefined
): SiteRolesType | undefined {
  if (!roleKey) {
    return undefined;
  }
  const cleanKey = roleKey.toLowerCase().trim();
  if (SITE_ROLES.hasOwnProperty(cleanKey)) {
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly public: "public"; readonly project_admin: "project_admin"; readonly designer: "designer"; readonly page_builder: "page_builder"; readonly editor: "editor"; readonly : "commenter"; readonly viewer: "viewer"; }'.
    return SITE_ROLES[cleanKey] as SiteRolesType;
  }
  return undefined;
}
